<template>
  <div class="notification-container">
    <!-- 头部区域 -->
    <div class="notification-header">
      <div class="header-content">
        <h2>
          <i class="el-icon-bell"></i>
          消息通知
        </h2>
        <div class="header-actions">
          <el-button
            type="text"
            size="small"
            @click="refreshAllRequests"
            :loading="isRefreshing"
            class="refresh-btn"
          >
            <i class="el-icon-refresh"></i>
            刷新
          </el-button>
        </div>
      </div>
    </div>

    <!-- 内容区域 -->
    <div class="notification-content">
      <!-- 加载状态 -->
      <div v-if="isLoading" class="loading-container">
        <div class="custom-loader">
          <div class="loader-ring">
            <div class="ring-segment"></div>
            <div class="ring-segment"></div>
            <div class="ring-segment"></div>
            <div class="ring-segment"></div>
          </div>
          <div class="loading-text">正在加载通知...</div>
        </div>
      </div>

      <!-- 主要内容 -->
      <div v-else>
        <!-- 好友请求分组 -->
        <div class="request-group">
          <div class="group-header" @click="toggleRequestsGroup">
            <div class="group-name">
              <i
                :class="requestsExpanded ? 'el-icon-arrow-down' : 'el-icon-arrow-right'"
                class="expand-icon"
              ></i>
              <i class="el-icon-user-solid group-icon"></i>
              <span>好友请求</span>
              <el-badge
                v-if="unreadCount > 0"
                :value="unreadCount"
                :max="99"
                class="request-badge"
              />
            </div>
            <div class="group-stats">
              <span class="stats-text">
                共 {{ totalRequestsCount }} 条
              </span>
            </div>
          </div>

          <!-- 分组内容 -->
          <transition name="slide-fade">
            <div class="group-content" v-show="requestsExpanded">
              <!-- 空状态 -->
              <div v-if="receivedRequests.length === 0 && sentRequests.length === 0" class="empty-state">
                <el-empty description="暂无好友请求" :image-size="120">
                  <template #description>
                    <div class="empty-description">
                      <p>还没有收到或发送任何好友请求</p>
                      <p class="empty-tip">去发现更多朋友吧！</p>
                    </div>
                  </template>
                </el-empty>
              </div>

              <!-- 收到的好友请求 -->
              <div class="request-section" v-if="receivedRequests.length > 0">
                <div class="section-header">
                  <div class="section-title">
                    <i class="el-icon-download"></i>
                    收到的好友请求
                    <span class="section-count">({{ receivedRequests.length }})</span>
                  </div>
                </div>
                <div class="requests-grid">
                  <div
                    class="request-item"
                    v-for="item in receivedRequests"
                    :key="'received-' + item.id"
                    :class="{ 'unread-item': item.isRead === 0 }"
                  >
                    <div class="user-info">
                      <div class="avatar-container">
                        <head-image
                          :id="item.fromUserId"
                          :url="item.fromHeadImage"
                          :name="item.fromNickName"
                          :size="avatarSize"
                          class="head-image"
                        />
                        <div v-if="item.isRead === 0" class="unread-dot"></div>
                      </div>

                      <div class="user-detail">
                        <div class="nickname">
                          <span class="display-name" :title="getNameTooltip(item.fromUserId, item.fromNickName)">
                            {{ getDisplayNameWithIcon(item.fromUserId, item.fromNickName) }}
                          </span>
                          <span
                            class="real-name"
                            v-if="getRemarkName(item.fromUserId) && getRemarkName(item.fromUserId) !== item.fromNickName"
                          >
                            （{{ item.fromNickName }}）
                          </span>
                          <span class="user-id">ID: {{ item.fromUserId }}</span>
                        </div>

                        <div class="request-msg" v-if="item.requestMsg">
                          <i class="el-icon-chat-line-square"></i>
                          {{ item.requestMsg }}
                        </div>

                        <div class="request-time-info">
                          <i class="el-icon-time"></i>
                          <span class="relative-time" :title="getFullTimestamp(item.createTime)">
                            {{ getRelativeTime(item.createTime) }}
                          </span>
                        </div>

                        <div class="request-status">
                          <!-- 处理状态 -->
                          <div v-if="item.status === 0" class="action-buttons">
                            <el-button
                              type="success"
                              size="small"
                              @click="showHandleConfirm(item.id, 1, item.fromNickName)"
                              :loading="processingRequestId === item.id"
                            >
                              <i class="el-icon-check"></i>
                              同意
                            </el-button>
                            <el-button
                              type="danger"
                              size="small"
                              @click="showHandleConfirm(item.id, 2, item.fromNickName)"
                              :loading="processingRequestId === item.id"
                            >
                              <i class="el-icon-close"></i>
                              拒绝
                            </el-button>
                          </div>
                          <div v-else-if="item.status === 1" class="status-badge accepted">
                            <i class="el-icon-success"></i>
                            已接受
                          </div>
                          <div v-else-if="item.status === 2" class="status-badge rejected">
                            <i class="el-icon-error"></i>
                            已拒绝
                          </div>
                          
                          <!-- 已读状态 - 独立于处理状态 -->
                          <div v-if="item.isRead === 0" class="read-status">
                            <el-button
                              type="info"
                              size="small"
                              @click="markRequestAsRead(item.id)"
                              :loading="processingRequestId === item.id"
                              class="mark-read-btn"
                            >
                              <i class="el-icon-view"></i>
                              标记已读
                            </el-button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- 发送的好友请求 -->
              <div class="request-section" v-if="sentRequests.length > 0">
                <div class="section-header">
                  <div class="section-title">
                    <i class="el-icon-upload2"></i>
                    发送的好友请求
                    <span class="section-count">({{ sentRequests.length }})</span>
                  </div>
                </div>
                <div class="requests-grid">
                  <div
                    class="request-item"
                    v-for="item in sentRequests"
                    :key="'sent-' + item.id"
                  >
                    <div class="user-info">
                      <div class="avatar-container">
                        <head-image
                          :id="item.toUserId"
                          :url="item.toHeadImage"
                          :name="item.toNickName"
                          :size="avatarSize"
                          class="head-image"
                        />
                      </div>

                      <div class="user-detail">
                        <div class="nickname">
                          <span class="display-name" :title="getNameTooltip(item.toUserId, item.toNickName)">
                            {{ getDisplayNameWithIcon(item.toUserId, item.toNickName) }}
                          </span>
                          <span
                            class="real-name"
                            v-if="getRemarkName(item.toUserId) && getRemarkName(item.toUserId) !== item.toNickName"
                          >
                            （{{ item.toNickName }}）
                          </span>
                          <span class="user-id">ID: {{ item.toUserId }}</span>
                        </div>

                        <div class="request-msg" v-if="item.requestMsg">
                          <i class="el-icon-chat-line-square"></i>
                          {{ item.requestMsg }}
                        </div>

                        <div class="request-time-info">
                          <i class="el-icon-time"></i>
                          <span class="relative-time" :title="getFullTimestamp(item.createTime)">
                            {{ getRelativeTime(item.createTime) }}
                          </span>
                        </div>

                        <div class="request-status">
                          <div v-if="item.status === 0" class="status-badge waiting">
                            <i class="el-icon-loading"></i>
                            等待处理
                          </div>
                          <div v-else-if="item.status === 1" class="status-badge accepted">
                            <i class="el-icon-success"></i>
                            已接受
                          </div>
                          <div v-else-if="item.status === 2" class="status-badge rejected">
                            <i class="el-icon-error"></i>
                            已拒绝
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </transition>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { listReceivedRequests, listSentRequests, handleFriendRequest, markAsRead } from '@/api/friendRequest'
import HeadImage from '@/components/common/HeadImage.vue'

export default {
  name: 'Notification',
  components: {
    HeadImage
  },
  data() {
    return {
      requestsExpanded: true, // 默认展开
      receivedRequests: [],
      sentRequests: [],
      unreadCount: 0,
      timeUpdateTimer: null, // 时间更新定时器
      isLoading: false, // 加载状态
      isRefreshing: false, // 刷新状态
      processingRequestId: null // 正在处理的请求ID
    }
  },
  created() {
    this.loadRequestsExpanded()
    this.fetchAllRequests()
    this.startTimeUpdateTimer()
  },
  beforeDestroy() {
    this.clearTimeUpdateTimer()
  },
  methods: {
    toggleRequestsGroup() {
      this.requestsExpanded = !this.requestsExpanded
      // 保存展开状态到本地存储
      try {
        localStorage.setItem('requests_expanded', this.requestsExpanded)
      } catch (error) {
        console.error('保存请求分组展开状态失败:', error)
      }
    },
    loadRequestsExpanded() {
      try {
        const expanded = localStorage.getItem('requests_expanded')
        this.requestsExpanded = expanded !== null ? JSON.parse(expanded) : true
      } catch (error) {
        this.requestsExpanded = true
      }
    },
    async refreshAllRequests() {
      this.isRefreshing = true
      try {
        await this.fetchAllRequests()
        this.$message.success('刷新成功')
      } catch (error) {
        this.$message.error('刷新失败')
      } finally {
        this.isRefreshing = false
      }
    },
    async fetchAllRequests() {
      this.isLoading = true
      try {
        await Promise.all([
          this.fetchReceivedRequests(),
          this.fetchSentRequests()
        ])
        this.calculateUnreadCount()
      } finally {
        this.isLoading = false
      }
    },
    async fetchReceivedRequests() {
      try {
        const response = await listReceivedRequests()
        this.receivedRequests = response || []
      } catch (error) {
        console.error('获取收到的好友请求失败:', error)
        this.receivedRequests = []
      }
    },
    async fetchSentRequests() {
      try {
        const response = await listSentRequests()
        this.sentRequests = response || []
      } catch (error) {
        console.error('获取发送的好友请求失败:', error)
        this.sentRequests = []
      }
    },
    calculateUnreadCount() {
      // 计算未读数量（isRead为0的收到的请求）
      this.unreadCount = this.receivedRequests.filter(item => item.isRead === 0).length
    },
    showHandleConfirm(requestId, status, fromNickName) {
      const action = status === 1 ? '同意' : '拒绝'
      this.$confirm(
        `确定要${action}来自"${fromNickName}"的好友请求吗？`,
        '确认操作',
        {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: status === 1 ? 'success' : 'warning'
        }
      ).then(() => {
        this.handleRequest(requestId, status)
      }).catch(() => {
        // 用户取消操作
      })
    },
    async handleRequest(requestId, status) {
      this.processingRequestId = requestId
      try {
        await handleFriendRequest({
          requestId,
          status
        })

        // 查找当前请求，如果未读则标记为已读
        const currentRequest = this.receivedRequests.find(item => item.id === requestId)
        if (currentRequest && currentRequest.isRead === 0) {
          await markAsRead(requestId)
        }

        this.$message({
          type: 'success',
          message: status === 1 ? '已同意好友请求' : '已拒绝好友请求'
        })
        await this.fetchAllRequests()
        // 通知其他组件刷新好友列表和已读状态
        this.$eventBus.$emit('friendListChanged')
        this.$eventBus.$emit('friendRequestRead')
      } catch (error) {
        this.$message({
          type: 'error',
          message: status === 1 ? '同意请求失败' : '拒绝请求失败'
        })
      } finally {
        this.processingRequestId = null
      }
    },
    async markRequestAsRead(requestId) {
      this.processingRequestId = requestId
      try {
        await markAsRead(requestId)
        this.$message({
          type: 'success',
          message: '已标记为已读'
        })
        await this.fetchAllRequests()
        // 通知其他组件刷新已读状态
        this.$eventBus.$emit('friendRequestRead')
      } catch (error) {
        this.$message({
          type: 'error',
          message: '标记已读失败'
        })
      } finally {
        this.processingRequestId = null
      }
    },
    formatTime(time) {
      if (!time) return ''
      const date = new Date(time)
      const now = new Date()
      const diff = now.getTime() - date.getTime()

      // 小于一天，显示时间
      if (diff < 24 * 60 * 60 * 1000) {
        return `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`
      }
      // 小于一年，显示月日
      if (diff < 365 * 24 * 60 * 60 * 1000) {
        return `${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`
      }
      // 大于一年，显示年月日
      return `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`
    },
    getStatusText(status, type) {
      const statusMap = {
        0: type === 'received' ? '待处理' : '等待对方处理',
        1: type === 'received' ? '已同意，成为好友' : '对方已同意，已成为好友',
        2: type === 'received' ? '已拒绝此请求' : '对方已拒绝此请求'
      }
      return statusMap[status] || '未知状态'
    },
    getRelativeTime(time) {
      if (!time) return ''
      const date = new Date(time)
      const now = new Date()
      const diff = now.getTime() - date.getTime()

      // 小于1分钟
      if (diff < 60 * 1000) {
        return '刚刚'
      }
      // 小于1小时
      if (diff < 60 * 60 * 1000) {
        const minutes = Math.floor(diff / (60 * 1000))
        return `${minutes}分钟前`
      }
      // 小于1天
      if (diff < 24 * 60 * 60 * 1000) {
        const hours = Math.floor(diff / (60 * 60 * 1000))
        return `${hours}小时前`
      }
      // 小于7天
      if (diff < 7 * 24 * 60 * 60 * 1000) {
        const days = Math.floor(diff / (24 * 60 * 60 * 1000))
        return `${days}天前`
      }
      // 小于30天
      if (diff < 30 * 24 * 60 * 60 * 1000) {
        const weeks = Math.floor(diff / (7 * 24 * 60 * 60 * 1000))
        return `${weeks}周前`
      }
      // 小于1年
      if (diff < 365 * 24 * 60 * 60 * 1000) {
        const months = Math.floor(diff / (30 * 24 * 60 * 60 * 1000))
        return `${months}个月前`
      }
      // 超过1年
      const years = Math.floor(diff / (365 * 24 * 60 * 60 * 1000))
      return `${years}年前`
    },
    getFullTimestamp(time) {
      if (!time) return ''
      const date = new Date(time)
      const year = date.getFullYear()
      const month = (date.getMonth() + 1).toString().padStart(2, '0')
      const day = date.getDate().toString().padStart(2, '0')
      const hours = date.getHours().toString().padStart(2, '0')
      const minutes = date.getMinutes().toString().padStart(2, '0')
      const seconds = date.getSeconds().toString().padStart(2, '0')
      const weekday = ['周日', '周一', '周二', '周三', '周四', '周五', '周六'][date.getDay()]

      return `${year}-${month}-${day} ${weekday} ${hours}:${minutes}:${seconds}`
    },
    getDisplayName(userId, originalName) {
      const remarkName = this.getRemarkName(userId)
      return remarkName || originalName
    },
    getDisplayNameWithIcon(userId, originalName) {
      const remarkName = this.getRemarkName(userId)
      if (remarkName && remarkName !== originalName) {
        return `🏷️ ${remarkName}`
      }
      return originalName
    },
    getNameTooltip(userId, originalName) {
      const remarkName = this.getRemarkName(userId)
      if (remarkName && remarkName !== originalName) {
        return `备注名字: ${remarkName}\n真实昵称: ${originalName}`
      }
      return `昵称: ${originalName}`
    },
    getRemarkName(userId) {
      // 从好友列表中查找对应的备注名字
      const friend = this.friends.find(f => f.id === userId)
      return friend?.remarkName || ''
    },
    startTimeUpdateTimer() {
      // 每分钟更新一次相对时间
      this.timeUpdateTimer = setInterval(() => {
        this.$forceUpdate() // 强制更新组件以刷新相对时间显示
      }, 60000) // 60秒更新一次
    },
    clearTimeUpdateTimer() {
      if (this.timeUpdateTimer) {
        clearInterval(this.timeUpdateTimer)
        this.timeUpdateTimer = null
      }
    }
  },
  computed: {
    avatarSize() {
      // 根据屏幕宽度动态设置头像大小
      return window.innerWidth <= 768 ? 45 : 50
    },
    friends() {
      // 获取好友列表用于查找备注名字
      return this.$store.state.friendStore.friends || []
    },
    totalRequestsCount() {
      return this.receivedRequests.length + this.sentRequests.length
    }
  }
}
</script>

<style lang="scss" scoped>
// 主容器
.notification-container {
  height: 100%;
  display: flex;
  flex-direction: column;
  background: linear-gradient(135deg, #f5f7fa 0%, #f0f2f5 100%);
}

// 头部样式
.notification-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.15);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    width: 200px;
    height: 200px;
    background: rgba(255, 255, 255, 0.1);
    border-radius: 50%;
    transform: translate(50%, -50%);
  }

  .header-content {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 20px 24px;
    position: relative;
    z-index: 1;

    h2 {
      margin: 0;
      font-size: 20px;
      font-weight: 600;
      display: flex;
      align-items: center;
      gap: 8px;

      i {
        font-size: 24px;
        animation: bell-ring 2s ease-in-out infinite;
      }
    }

    .header-actions {
      .refresh-btn {
        color: white;
        border: 1px solid rgba(255, 255, 255, 0.3);
        background: rgba(255, 255, 255, 0.1);
        backdrop-filter: blur(10px);
        border-radius: 20px;
        padding: 8px 16px;
        transition: all 0.3s ease;

        &:hover {
          background: rgba(255, 255, 255, 0.2);
          transform: translateY(-1px);
        }

        i {
          margin-right: 4px;
        }
      }
    }
  }
}

// 内容容器
.notification-content {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  min-height: 0;
}

// 加载状态
.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 300px;

  .custom-loader {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 20px;

    .loader-ring {
      position: relative;
      width: 60px;
      height: 60px;
      border-radius: 50%;

      .ring-segment {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        border: 3px solid transparent;
        border-radius: 50%;
        animation: ring-rotate 1.5s linear infinite;

        &:nth-child(1) {
          border-top-color: #667eea;
          animation-delay: 0s;
        }

        &:nth-child(2) {
          border-right-color: #764ba2;
          animation-delay: 0.3s;
        }

        &:nth-child(3) {
          border-bottom-color: #667eea;
          animation-delay: 0.6s;
          opacity: 0.7;
        }

        &:nth-child(4) {
          border-left-color: #764ba2;
          animation-delay: 0.9s;
          opacity: 0.5;
        }
      }
    }

    .loading-text {
      color: #667eea;
      font-size: 14px;
      font-weight: 500;
      animation: text-pulse 2s ease-in-out infinite;
    }
  }
}

// 请求分组
.request-group {
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  border: 1px solid rgba(102, 126, 234, 0.1);
}

// 分组头部
.group-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  background: linear-gradient(135deg, #f8f9ff 0%, #f0f4ff 100%);
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;

  &:hover {
    background: linear-gradient(135deg, #f0f4ff 0%, #e8f0ff 100%);
  }

  .group-name {
    display: flex;
    align-items: center;
    font-size: 18px;
    font-weight: 600;
    color: #2c3e50;
    gap: 12px;

    .expand-icon {
      font-size: 16px;
      transition: all 0.3s ease;
      color: #667eea;
    }

    .group-icon {
      font-size: 20px;
      color: #667eea;
    }

    .request-badge {
      margin-left: 8px;
    }
  }

  .group-stats {
    .stats-text {
      font-size: 14px;
      color: #8492a6;
      background: rgba(102, 126, 234, 0.1);
      padding: 4px 12px;
      border-radius: 12px;
    }
  }
}

// 分组内容
.group-content {
  background: #fafbff;
}

// 空状态
.empty-state {
  padding: 40px 20px;

  .empty-description {
    p {
      margin: 8px 0;
    }

    .empty-tip {
      color: #999;
      font-size: 14px;
    }
  }
}

// 请求区域
.request-section {
  &:not(:last-child) {
    border-bottom: 1px solid #f0f0f0;
  }

  .section-header {
    padding: 20px 24px 16px;

    .section-title {
      font-size: 16px;
      font-weight: 600;
      color: #34495e;
      display: flex;
      align-items: center;
      gap: 8px;

      i {
        color: #667eea;
        font-size: 18px;
      }

      .section-count {
        color: #8492a6;
        font-weight: normal;
        font-size: 14px;
      }
    }
  }
}

// 请求网格
.requests-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 16px;
  padding: 0 24px 24px;
  align-items: start;
}

// 请求项
.request-item {
  background: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  border: 1px solid #f0f0f0;
  transition: all 0.3s ease;
  position: relative;
  min-height: 180px;
  display: flex;
  flex-direction: column;

  &:hover {
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
    transform: translateY(-2px);
    border-color: #667eea;
  }

  &.unread-item {
    border-left: 4px solid #667eea;
    background: linear-gradient(135deg, #fff 0%, #f8f9ff 100%);
  }
}

// 用户信息
.user-info {
  display: flex;
  gap: 16px;
  flex: 1;
  min-height: 0;

  .avatar-container {
    position: relative;
    flex-shrink: 0;

    .head-image {
      border: 2px solid #f0f0f0;
      transition: border-color 0.3s ease;
    }

    .unread-dot {
      position: absolute;
      top: -2px;
      right: -2px;
      width: 12px;
      height: 12px;
      background: #f56c6c;
      border-radius: 50%;
      border: 2px solid white;
      animation: pulse-dot 2s ease-in-out infinite;
    }
  }

  .user-detail {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    justify-content: space-between;

    .nickname {
      margin-bottom: 8px;
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: 8px;
      line-height: 1.4;

      .display-name {
        font-size: 16px;
        font-weight: 600;
        color: #2c3e50;
        cursor: help;
        transition: color 0.2s ease;
        word-break: break-word;

        &:hover {
          color: #667eea;
        }
      }

      .real-name {
        font-size: 14px;
        color: #8492a6;
        font-style: italic;
        word-break: break-word;
      }

      .user-id {
        font-size: 12px;
        color: #bdc3c7;
        background: #f8f9fa;
        padding: 2px 8px;
        border-radius: 8px;
        font-family: 'Courier New', monospace;
        white-space: nowrap;
      }
    }

    .request-msg {
      font-size: 14px;
      color: #5a6c7d;
      line-height: 1.5;
      margin-bottom: 12px;
      padding: 8px 12px;
      background: #f8f9fa;
      border-radius: 8px;
      border-left: 3px solid #667eea;
      display: flex;
      align-items: flex-start;
      gap: 8px;
      word-break: break-word;
      overflow-wrap: break-word;

      i {
        color: #667eea;
        margin-top: 2px;
        flex-shrink: 0;
      }
    }

    .request-time-info {
      margin-bottom: 16px;
      display: flex;
      align-items: center;
      gap: 6px;
      color: #8492a6;
      font-size: 13px;

      i {
        color: #bdc3c7;
        flex-shrink: 0;
      }

      .relative-time {
        white-space: nowrap;
      }
    }

          .request-status {
        margin-top: auto;

        .action-buttons {
          display: flex;
          gap: 12px;
          flex-wrap: wrap;
          margin-bottom: 8px;

          .el-button {
            border-radius: 8px;
            font-weight: 500;
            transition: all 0.3s ease;
            flex: 1;
            min-width: 80px;

            &:hover {
              transform: translateY(-1px);
            }
          }
        }

        .read-status {
          margin-top: 8px;

          .mark-read-btn {
            background: #f0f2f5;
            border-color: #d1d5db;
            color: #6b7280;
            border-radius: 8px;
            font-weight: 500;
            transition: all 0.3s ease;
            width: 100%;

            &:hover {
              background: #e5e7eb;
              border-color: #9ca3af;
              color: #4b5563;
              transform: translateY(-1px);
            }
          }
        }

      .status-badge {
        display: flex;
        align-items: center;
        gap: 6px;
        padding: 6px 12px;
        border-radius: 8px;
        font-size: 13px;
        font-weight: 500;
        width: fit-content;

        &.accepted {
          background: linear-gradient(135deg, #d4edda 0%, #c3e6cb 100%);
          color: #155724;
          i { color: #28a745; }
        }

        &.rejected {
          background: linear-gradient(135deg, #f8d7da 0%, #f5c6cb 100%);
          color: #721c24;
          i { color: #dc3545; }
        }

        &.waiting {
          background: linear-gradient(135deg, #fff3cd 0%, #ffeaa7 100%);
          color: #856404;
          i {
            color: #ffc107;
            animation: spin 1s linear infinite;
          }
        }
      }
    }
  }
}

// 动画定义
@keyframes bell-ring {
  0%, 100% { transform: rotate(0deg); }
  10%, 30%, 50%, 70%, 90% { transform: rotate(-10deg); }
  20%, 40%, 60%, 80% { transform: rotate(10deg); }
}

@keyframes pulse-dot {
  0%, 100% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.2);
    opacity: 0.8;
  }
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

@keyframes ring-rotate {
  0% {
    transform: rotate(0deg);
    opacity: 1;
  }
  50% {
    opacity: 0.3;
  }
  100% {
    transform: rotate(360deg);
    opacity: 1;
  }
}

@keyframes text-pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.6;
  }
}

// 过渡动画
.slide-fade-enter-active, .slide-fade-leave-active {
  transition: all 0.3s ease;
}
.slide-fade-enter, .slide-fade-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

// 响应式设计
@media (max-width: 1200px) {
  .requests-grid {
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 14px;
  }

  .request-item {
    min-height: 160px;
    padding: 18px;
  }
}

@media (max-width: 992px) {
  .requests-grid {
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 12px;
    padding: 0 20px 20px;
  }

  .request-item {
    min-height: 150px;
    padding: 16px;
  }
}

@media (max-width: 768px) {
  .notification-header {
    .header-content {
      padding: 16px 20px;

      h2 {
        font-size: 18px;
      }
    }
  }

  .notification-content {
    padding: 16px;
  }

  .group-header {
    padding: 16px 20px;

    .group-name {
      font-size: 16px;
    }
  }

  .requests-grid {
    grid-template-columns: 1fr;
    padding: 0 16px 16px;
    gap: 12px;
  }

  .request-item {
    padding: 16px;
    min-height: 140px;
  }

  .user-info {
    gap: 12px;

    .user-detail {
      .nickname {
        .display-name {
          font-size: 15px;
        }

        .real-name {
          font-size: 13px;
        }

        .user-id {
          font-size: 11px;
          padding: 1px 6px;
        }
      }

      .request-msg {
        font-size: 13px;
        padding: 6px 10px;
        margin-bottom: 10px;
      }

      .request-time-info {
        font-size: 12px;
        margin-bottom: 12px;
      }

              .request-status {
          .action-buttons {
            gap: 8px;

            .el-button {
              padding: 8px 12px;
              font-size: 13px;
            }
          }

          .read-status {
            .mark-read-btn {
              padding: 8px 12px;
              font-size: 13px;
            }
          }

          .status-badge {
            font-size: 12px;
            padding: 4px 8px;
          }
        }
    }
  }
}

@media (max-width: 480px) {
  .notification-header {
    .header-content {
      padding: 12px 16px;
      flex-direction: column;
      gap: 12px;
      align-items: flex-start;
    }
  }

  .notification-content {
    padding: 12px;
  }

  .group-header {
    padding: 12px 16px;

    .group-name {
      font-size: 15px;
      gap: 8px;
    }

    .group-stats {
      .stats-text {
        font-size: 12px;
        padding: 2px 8px;
      }
    }
  }

  .requests-grid {
    padding: 0 12px 12px;
    gap: 10px;
  }

  .request-item {
    padding: 12px;
    min-height: 120px;
  }

  .user-info {
    gap: 10px;

    .avatar-container {
      .head-image {
        // 在小屏幕上稍微缩小头像
      }
    }

    .user-detail {
      .nickname {
        gap: 6px;
        margin-bottom: 6px;

        .display-name {
          font-size: 14px;
        }

        .real-name {
          font-size: 12px;
        }

        .user-id {
          font-size: 10px;
          padding: 1px 4px;
        }
      }

      .request-msg {
        font-size: 12px;
        padding: 6px 8px;
        margin-bottom: 8px;
      }

      .request-time-info {
        font-size: 11px;
        margin-bottom: 10px;
      }

              .request-status {
          .action-buttons {
            flex-direction: column;
            gap: 6px;

            .el-button {
              width: 100%;
              padding: 6px 12px;
              font-size: 12px;
              min-width: auto;
            }
          }

          .read-status {
            .mark-read-btn {
              padding: 6px 12px;
              font-size: 12px;
            }
          }

          .status-badge {
            font-size: 11px;
            padding: 3px 6px;
          }
        }
    }
  }
}

@media (max-width: 320px) {
  .requests-grid {
    padding: 0 8px 8px;
  }

  .request-item {
    padding: 10px;
    min-height: 110px;
  }

  .user-info {
    gap: 8px;

    .user-detail {
      .nickname {
        .display-name {
          font-size: 13px;
        }
      }

      .request-msg {
        font-size: 11px;
        padding: 4px 6px;
      }
    }
  }
}
</style>
